import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getLoggedInUserString } from "./utils.js";
import { getLevel1ByGame } from "../services/GameService.js";
import "./GamesGrid.css"; // Add CSS styling for grid if necessary

import axios from "axios";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const GameGrid = ({
  gameTypeId, // Now received as a prop
  imageWidth = 200,
  imageHeight = 100,
  onGamesCount, // optional: report how many games loaded (for filler scaling)
}) => {
  const [games, setGames] = useState([]);
  const carouselRef = useRef(null); // Ref for the carousel element
  const scrollIntervalRef = useRef(null); // Ref for the scroll interval
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const appParamsRef = useRef("");

  useEffect(() => {
    console.log(games);
  }, [games]);

  useEffect(() => {
    // Fetch current user data, if logged in
    const loggedInUser = getLoggedInUserString(); // Implement this function to fetch user data
  }, []);

  const handleScroll = useCallback((scrollOffset) => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft += scrollOffset;
    }
  }, []);

  const startScroll = useCallback((direction) => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
    scrollIntervalRef.current = setInterval(() => {
      handleScroll(direction === "left" ? -10 : 10); // Slow scroll
    }, 50); // Adjust interval and scroll speed as needed
  }, [handleScroll]);

  const stopScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
  }, []);

  const checkScrollability = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollWidth > clientWidth + scrollLeft);
    }
  }, []);

  const handleClickScroll = useCallback((direction) => {
    // Clear any active continuous scroll interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }
    if (carouselRef.current) {
      const scrollAmount = direction === "left" ? -imageWidth * 2 : imageWidth * 2; // Scroll by two card widths
      carouselRef.current.scrollLeft += scrollAmount;
    }
    // If still hovering, restart the continuous scroll after the click with a slight delay
    // Use a single setTimeout to manage the restart logic
    setTimeout(() => {
      if (direction === "left" && isHoveringLeft) {
        startScroll("left");
      } else if (direction === "right" && isHoveringRight) {
        startScroll("right");
      }
    }, 150); // 150ms delay
  }, [imageWidth, isHoveringLeft, isHoveringRight, startScroll]);

  const fetchAppParams = async (gameId) => {
    let appParams;
    /*
    if (currentUser) {
      appParams = fetchUserParams(gameId);
    } else {
      const level1 = await getLevel1ByGame(gameId);
      appParams = level1?.level_parameters || "";
    }
      */
    return appParams;
  };



  const navigate = useNavigate();

  // Fetch games based on selected game type
  useEffect(() => {
    if (gameTypeId) {
      fetch(`${SERVER_URL}/api/games-by-type/?game_type=${gameTypeId}`)
        .then((response) => response.json())
        .then((data) => {
          setGames(data);
          if (onGamesCount) onGamesCount(data.length);
        })
        .catch((error) => console.error("Error fetching games:", error));
    }
  }, [gameTypeId]);

  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (carouselElement) {
      carouselElement.addEventListener("scroll", checkScrollability);
      // Initial check when component mounts or games change
      checkScrollability();
    }

    return () => {
      if (carouselElement) {
        carouselElement.removeEventListener("scroll", checkScrollability);
      }
    };
  }, [games, checkScrollability]); // Re-run when games change to re-evaluate scrollability

  // Handle clicking on a game image to navigate to the game's route
  const handleImageClick = async (gameRoutePath, gameId) => {
    if (gameRoutePath) {
      const levelParams = await fetchAppParams(gameId); // Ensure this returns the params as a string
      appParamsRef.current = levelParams;

      navigate(`/${gameRoutePath}`, { state: appParamsRef.current }); // Navigate to the game's specific route path
    } else {
      console.error("Game route path is undefined!");
    }
  };

  return (
    <div className="games-carousel-container">
      {canScrollLeft && (
        <div
          className="scroll-zone scroll-zone-left"
          onMouseEnter={() => {
            setIsHoveringLeft(true);
            startScroll("left");
          }}
          onMouseLeave={() => {
            setIsHoveringLeft(false);
            stopScroll();
          }}
          onClick={() => handleClickScroll("left")}
          aria-label="Scroll left"
        >
        <ChevronLeft className="scroll-arrow" size={36} strokeWidth={2.5} aria-hidden="true" />
        </div>
      )}
      <div className="games-carousel" ref={carouselRef}>
        {games.map((game, i) => (
          <div
            key={game.game_id}
            className="game-item-card"
            style={{ "--tile-tilt": `${(i % 3) - 1}deg` }}
            onClick={() => handleImageClick(game.game_route_path, game.game_id)}
          >
            <div className="game-item-frame">
              <img
                src={`${SERVER_URL}/media/${game.game_image}`}
                alt={game.game_name}
                className="game-item-image"
              />
            </div>
            <p>{game.game_name}</p>
            {/* Hover pop-out with the full name — an overlay, so hovering never resizes the tile. */}
            <span className="game-item-name-full" aria-hidden="true">{game.game_name}</span>
          </div>
        ))}
      </div>
      {canScrollRight && (
        <div
          className="scroll-zone scroll-zone-right"
          onMouseEnter={() => {
            setIsHoveringRight(true);
            startScroll("right");
          }}
          onMouseLeave={() => {
            setIsHoveringRight(false);
            stopScroll();
          }}
          onClick={() => handleClickScroll("right")}
          aria-label="Scroll right"
        >
        <ChevronRight className="scroll-arrow" size={36} strokeWidth={2.5} aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export default GameGrid;
